import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, Link } from 'react-router-dom';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { CartProvider, useCart } from './context/CartContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import { SiteSettingsProvider, useSiteSettings } from './context/SiteSettingsContext';
import { formatNumberPersian } from './utils/mobileUtils';
import { formatPrice } from './utils/priceUtils';
import { getImageUrl, formatSocialLink } from './utils/urlUtils';
import { apiFetch } from './utils/api';
import ProductDetail from './pages/ProductDetail';
import Dashboard from './pages/Dashboard';
import Cart from './pages/Cart';
import Login from './pages/Login';
import Checkout from './pages/Checkout';
import Payment from './pages/Payment';
import PaymentResult from './pages/PaymentResult';
import Invoice from './pages/Invoice';
import Profile from './pages/Profile';
import './App.css';

function ProductList() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('همه');
  const [searchTerm, setSearchTerm] = useState('');
  const { isDarkMode, toggleTheme } = useTheme();
  const { getTotalItems } = useCart();
  const { user } = useAuth();
  const { settings } = useSiteSettings();
  const navigate = useNavigate();

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await apiFetch('/api/products');
      const data = await response.json();
      setProducts(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching products:', error);
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await apiFetch('/api/categories');
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const categoryNames = ['همه', ...categories.map(cat => cat.name)];

  const filteredProducts = products.filter(product => {
    const productCategoryName = product.category?.name;
    const matchesCategory = selectedCategory === 'همه' || productCategoryName === selectedCategory;
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const getPlaceholderImage = (productName) => {
    const text = productName || 'Product';
    const svg = `
      <svg width="300" height="300" xmlns="http://www.w3.org/2000/svg">
        <rect width="300" height="300" fill="#f0f0f0"/>
        <text x="50%" y="50%" font-family="Vazirmatn, sans-serif" font-size="20" fill="#999" text-anchor="middle" dominant-baseline="middle">${text}</text>
      </svg>
    `.trim();
    return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg)))}`;
  };

  const handleProductClick = (productId) => {
    navigate(`/product/${productId}`);
  };

  const enamadHref = settings.enamad_inquiry_url || null;
  const zibalFooterHref = (() => {
    const u = (settings.zibal_footer_url || '').trim();
    return u || 'https://www.zibal.ir';
  })();

  if (loading) {
    return (
      <div className={`App ${isDarkMode ? 'dark' : ''}`}>
        <div className="loading">در حال بارگذاری...</div>
      </div>
    );
  }

  return (
    <div className={`App ${isDarkMode ? 'dark' : ''}`}>
      <header className="App-header">
        <div className="header-top">
          {settings.logo_url && (
            <img 
              src={getImageUrl(settings.logo_url)} 
              alt={settings.site_name || 'لوگو'} 
              className="header-logo"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          )}
          <h1>{settings.site_name || 'فروشگاه اینترنتی'}</h1>
          <div className="header-actions">
            <Link to="/cart" className="icon-link cart-link" title="سبد خرید" data-tooltip="سبد خرید">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="9" cy="21" r="1"></circle>
                <circle cx="20" cy="21" r="1"></circle>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
              </svg>
              {getTotalItems() > 0 && (
                <span className="cart-badge">{formatNumberPersian(getTotalItems())}</span>
              )}
            </Link>
            {user ? (
              <Link to="/dashboard" className="icon-link dashboard-link" title="پنل کاربری" data-tooltip="پنل کاربری">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </Link>
            ) : (
              <Link to="/login" className="icon-link dashboard-link" title="ورود" data-tooltip="ورود">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </Link>
            )}
            <button 
              className="theme-toggle" 
              onClick={toggleTheme} 
              title={isDarkMode ? 'روشن کردن تم' : 'تاریک کردن تم'}
              data-tooltip="تغییر تم"
            >
              {isDarkMode ? '☀️' : '🌙'}
            </button>
          </div>
        </div>
        <div className="search-bar">
          <input
            type="text"
            placeholder="جستجوی محصول..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="category-filter">
          {categoryNames.map(category => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={selectedCategory === category ? 'active' : ''}
            >
              {category}
            </button>
          ))}
        </div>
      </header>

      <main className="products-container">
        {filteredProducts.length === 0 ? (
          <div className="no-products">محصولی یافت نشد</div>
        ) : (
          <div className="products-grid">
            {filteredProducts.map(product => (
              <div 
                key={product.id} 
                className="product-card"
                onClick={() => handleProductClick(product.id)}
              >
                <div className="product-image">
                  <img 
                    src={getImageUrl(product.imageUrl) || getPlaceholderImage(product.name)} 
                    alt={product.name}
                    onError={(e) => {
                      e.target.src = getPlaceholderImage(product.name);
                    }}
                  />
                </div>
                <div className="product-info">
                  <h3>{product.name}</h3>
                  <p className="product-description">{product.description}</p>
                  <div className="product-details">
                    <span className="product-price">{formatPrice(product.price)}</span>
                    <span className="product-stock">
                      {product.stock > 0 ? `موجود` : 'ناموجود'}
                    </span>
                  </div>
                  <div className="product-category-small">
                    {product.category?.name || 'بدون دسته‌بندی'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <footer className="App-footer">
        <div className="footer-content">
          <div className="footer-section">
            <h3>{settings.site_name || 'فروشگاه اینترنتی'}</h3>
            {settings.site_description && (
              <p className="footer-description">{settings.site_description}</p>
            )}
          </div>
          
          <div className="footer-section">
            <h4>پشتیبانی</h4>
            {settings.support && (
              <p className="footer-support">{settings.support}</p>
            )}
            <div className="social-links">
              {settings.telegram_channel && (
                <a 
                  href={formatSocialLink('telegram', settings.telegram_channel)} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="social-link telegram"
                  title="تلگرام"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295-.002 0-.003 0-.005 0l.213-3.054 5.56-5.022c.24-.213-.054-.334-.373-.121l-6.869 4.326-2.96-.924c-.64-.203-.658-.64.135-.954l11.566-4.458c.538-.196 1.006.128.832.941z"/>
                  </svg>
                </a>
              )}
              {settings.instagram_channel && (
                <a 
                  href={formatSocialLink('instagram', settings.instagram_channel)} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="social-link instagram"
                  title="اینستاگرام"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                  </svg>
                </a>
              )}
              {settings.whatsapp_channel && (
                <a 
                  href={formatSocialLink('whatsapp', settings.whatsapp_channel)} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="social-link whatsapp"
                  title="واتساپ"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                </a>
              )}
              {settings.bale_channel && (
                <a 
                  href={formatSocialLink('bale', settings.bale_channel)} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="social-link bale"
                  title="پیام‌رسان بله"
                >
                  <img 
                    src="/images/bale.png" 
                    alt="پیام‌رسان بله" 
                    className="social-icon-image"
                  />
                </a>
              )}
              {settings.rubika_channel && (
                <a 
                  href={formatSocialLink('rubika', settings.rubika_channel)} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="social-link rubika"
                  title="روبیکا"
                >
                  <img 
                    src="/images/rubika.png" 
                    alt="روبیکا" 
                    className="social-icon-image"
                  />
                </a>
              )}
            </div>
          </div>
          
          <div className="footer-section">
            <h4>نماد اعتماد</h4>
            <div className="trust-badges">
              {enamadHref && (
                <a
                  referrerPolicy="origin"
                  target="_blank"
                  href={enamadHref}
                  title="استعلام نماد اعتماد الکترونیک"
                >
                  <img 
                    src="/images/enamad.png" 
                    alt="نماد اعتماد الکترونیک" 
                    className="trust-logo"
                    style={{ cursor: 'pointer' }}
                  />
                </a>
              )}
              <a
                href={zibalFooterHref}
                target="_blank"
                rel="noopener noreferrer"
                title="درگاه پرداخت زیبال"
              >
                <img 
                  src="/images/zibal.png" 
                  alt="درگاه پرداخت زیبال" 
                  className="trust-logo"
                />
              </a>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; {new Date().getFullYear()} {settings.site_name || 'فروشگاه اینترنتی'}. تمامی حقوق محفوظ است.</p>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <NotificationProvider>
        <SiteSettingsProvider>
          <AuthProvider>
            <CartProvider>
              <Router>
                <Routes>
                  <Route path="/" element={<ProductList />} />
                  <Route path="/product/:id" element={<ProductDetail />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/cart" element={<Cart />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/checkout" element={<Checkout />} />
                  <Route path="/payment/:orderId" element={<Payment />} />
                  <Route path="/payment/result" element={<PaymentResult />} />
                  <Route path="/invoice/:id" element={<Invoice />} />
                  <Route path="/profile" element={<Profile />} />
                </Routes>
              </Router>
            </CartProvider>
          </AuthProvider>
        </SiteSettingsProvider>
      </NotificationProvider>
    </ThemeProvider>
  );
}

export default App;
